import React, { useState } from 'react';
import { UserIcon, MailIcon, MessageSquareIcon, BellIcon, EditIcon, SaveIcon, XIcon } from 'lucide-react';
import { UserNotificationPreference, NotificationEvent } from './NotificationsManagement';
import { formatDateTime } from '../../utils/dateFormatter';
interface UserPreferencesProps {
  userPreferences: UserNotificationPreference[];
  setUserPreferences: React.Dispatch<React.SetStateAction<UserNotificationPreference[]>>;
  events: NotificationEvent[];
  locale: string;
}
export function UserPreferences({
  userPreferences,
  setUserPreferences,
  events,
  locale
}: UserPreferencesProps) {
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [editFormData, setEditFormData] = useState<Partial<UserNotificationPreference>>({});
  const handleEditUser = (userId: string) => {
    const user = userPreferences.find(pref => pref.userId === userId);
    if (user) {
      setEditFormData({
        ...user
      });
      setEditingUserId(userId);
    }
  };
  const handleCancelEdit = () => {
    setEditingUserId(null);
    setEditFormData({});
  };
  const handleSavePreferences = () => {
    if (!editingUserId || !editFormData) return;
    setUserPreferences(userPreferences.map(pref => pref.userId === editingUserId ? {
      ...pref,
      ...editFormData,
      updatedAt: new Date().toISOString()
    } : pref));
    setEditingUserId(null);
    setEditFormData({});
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {
      name,
      value,
      type
    } = e.target;
    const checked = type === 'checkbox' ? (e.target as HTMLInputElement).checked : undefined;
    setEditFormData({
      ...editFormData,
      [name]: type === 'checkbox' ? checked : value
    });
  };
  const handleToggleEvent = (eventId: string) => {
    const mutedEvents = editFormData.mutedEvents || [];
    if (mutedEvents.includes(eventId)) {
      setEditFormData({
        ...editFormData,
        mutedEvents: mutedEvents.filter(id => id !== eventId)
      });
    } else {
      setEditFormData({
        ...editFormData,
        mutedEvents: [...mutedEvents, eventId]
      });
    }
  };
  const getChannelIcon = (channel: string) => {
    switch (channel) {
      case 'teams':
        return <MessageSquareIcon className="h-5 w-5 text-blue-500" data-id="element-19568" />;
      case 'slack':
        return <MessageSquareIcon className="h-5 w-5 text-purple-500" data-id="element-19569" />;
      case 'email':
        return <MailIcon className="h-5 w-5 text-gray-500" data-id="element-19570" />;
      case 'in-app':
        return <BellIcon className="h-5 w-5 text-indigo-500" data-id="element-19571" />;
      default:
        return <BellIcon className="h-5 w-5 text-gray-500" data-id="element-19572" />;
    }
  };
  return <div data-id="element-19573">
      <div className="mb-6" data-id="element-19574">
        <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-19575">
          User Notification Preferences
        </h3>
        <p className="mt-1 text-sm text-gray-500" data-id="element-19576">
          Configure default notification preferences for users
        </p>
      </div>
      <div className="bg-white shadow overflow-hidden sm:rounded-md" data-id="element-19577">
        <ul className="divide-y divide-gray-200" data-id="element-19578">
          {userPreferences.length === 0 ? <li className="px-6 py-10 text-center" data-id="element-19579">
              <p className="text-sm text-gray-500" data-id="element-19580">
                No user preferences configured yet.
              </p>
            </li> : userPreferences.map(pref => <li key={pref.id} data-id="element-19581">
                {editingUserId === pref.userId ? <div className="px-4 py-4 sm:px-6" data-id="element-19582">
                    <div className="flex justify-between items-center mb-4" data-id="element-19583">
                      <div className="flex items-center" data-id="element-19584">
                        <UserIcon className="h-5 w-5 text-gray-400 mr-2" data-id="element-19585" />
                        <h4 className="text-sm font-medium text-gray-900" data-id="element-19586">
                          {pref.userName}
                        </h4>
                      </div>
                      <div className="flex space-x-2" data-id="element-19587">
                        <button type="button" onClick={handleSavePreferences} className="inline-flex items-center p-1 border border-transparent rounded-full shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-19588">
                          <SaveIcon className="h-4 w-4" aria-hidden="true" data-id="element-19589" />
                        </button>
                        <button type="button" onClick={handleCancelEdit} className="inline-flex items-center p-1 border border-transparent rounded-full shadow-sm text-white bg-gray-600 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500" data-id="element-19590">
                          <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-19591" />
                        </button>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-19592">
                      <div data-id="element-19593">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-19594">
                          Preferred Channel
                        </label>
                        <select name="preferredChannel" value={editFormData.preferredChannel} onChange={handleChange} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-19595">
                          <option value="teams" data-id="element-19596">Microsoft Teams</option>
                          <option value="slack" data-id="element-19597">Slack</option>
                          <option value="email" data-id="element-19598">Email</option>
                          <option value="in-app" data-id="element-19599">In-App</option>
                        </select>
                      </div>
                    </div>
                    <div className="space-y-3 mb-4" data-id="element-19600">
                      <div className="relative flex items-start" data-id="element-19601">
                        <div className="flex items-center h-5" data-id="element-19602">
                          <input id="receive-mentions" name="receiveMentions" type="checkbox" checked={editFormData.receiveMentions} onChange={handleChange} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded" data-id="element-19603" />
                        </div>
                        <div className="ml-3 text-sm" data-id="element-19604">
                          <label htmlFor="receive-mentions" className="font-medium text-gray-700" data-id="element-19605">
                            Receive Mentions
                          </label>
                          <p className="text-gray-500" data-id="element-19606">
                            Notifications when you are mentioned in notes or
                            comments
                          </p>
                        </div>
                      </div>
                      <div className="relative flex items-start" data-id="element-19607">
                        <div className="flex items-center h-5" data-id="element-19608">
                          <input id="receive-approvals" name="receiveApprovalReminders" type="checkbox" checked={editFormData.receiveApprovalReminders} onChange={handleChange} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded" data-id="element-19609" />
                        </div>
                        <div className="ml-3 text-sm" data-id="element-19610">
                          <label htmlFor="receive-approvals" className="font-medium text-gray-700" data-id="element-19611">
                            Receive Approval Reminders
                          </label>
                          <p className="text-gray-500" data-id="element-19612">
                            Notifications for documents requiring your approval
                          </p>
                        </div>
                      </div>
                      <div className="relative flex items-start" data-id="element-19613">
                        <div className="flex items-center h-5" data-id="element-19614">
                          <input id="receive-alerts" name="receiveSystemAlerts" type="checkbox" checked={editFormData.receiveSystemAlerts} onChange={handleChange} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded" data-id="element-19615" />
                        </div>
                        <div className="ml-3 text-sm" data-id="element-19616">
                          <label htmlFor="receive-alerts" className="font-medium text-gray-700" data-id="element-19617">
                            Receive System Alerts
                          </label>
                          <p className="text-gray-500" data-id="element-19618">
                            Notifications for system events and maintenance
                          </p>
                        </div>
                      </div>
                    </div>
                    <div data-id="element-19619">
                      <h5 className="text-sm font-medium text-gray-700 mb-2" data-id="element-19620">
                        Muted Events
                      </h5>
                      <div className="space-y-2 max-h-40 overflow-y-auto" data-id="element-19621">
                        {events.map(event => <div key={event.id} className="relative flex items-start" data-id="element-19622">
                            <div className="flex items-center h-5" data-id="element-19623">
                              <input id={`mute-event-${event.id}`} type="checkbox" checked={(editFormData.mutedEvents || []).includes(event.id)} onChange={() => handleToggleEvent(event.id)} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded" data-id="element-19624" />
                            </div>
                            <div className="ml-3 text-sm" data-id="element-19625">
                              <label htmlFor={`mute-event-${event.id}`} className="font-medium text-gray-700" data-id="element-19626">
                                {event.name}
                              </label>
                            </div>
                          </div>)}
                      </div>
                    </div>
                  </div> : <div className="px-4 py-4 sm:px-6" data-id="element-19627">
                    <div className="flex items-center justify-between" data-id="element-19628">
                      <div className="flex items-center" data-id="element-19629">
                        <UserIcon className="h-5 w-5 text-gray-400 mr-2" data-id="element-19630" />
                        <p className="text-sm font-medium text-indigo-600 truncate" data-id="element-19631">
                          {pref.userName}
                        </p>
                      </div>
                      <div data-id="element-19632">
                        <button type="button" onClick={() => handleEditUser(pref.userId)} className="inline-flex items-center p-1 border border-transparent rounded-full shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-19633">
                          <EditIcon className="h-4 w-4" aria-hidden="true" data-id="element-19634" />
                        </button>
                      </div>
                    </div>
                    <div className="mt-2 sm:flex sm:justify-between" data-id="element-19635">
                      <div className="sm:flex" data-id="element-19636">
                        <p className="flex items-center text-sm text-gray-500" data-id="element-19637">
                          <MailIcon className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" aria-hidden="true" data-id="element-19638" />
                          {pref.userEmail}
                        </p>
                      </div>
                      <div className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0" data-id="element-19639">
                        <div className="flex items-center" data-id="element-19640">
                          {getChannelIcon(pref.preferredChannel)}
                          <span className="ml-1 capitalize" data-id="element-19641">
                            {pref.preferredChannel === 'in-app' ? 'In-App' : pref.preferredChannel}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="mt-2" data-id="element-19642">
                      <div className="flex space-x-2" data-id="element-19643">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${pref.receiveMentions ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-19644">
                          {pref.receiveMentions ? 'Mentions On' : 'Mentions Off'}
                        </span>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${pref.receiveApprovalReminders ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-19645">
                          {pref.receiveApprovalReminders ? 'Approvals On' : 'Approvals Off'}
                        </span>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${pref.receiveSystemAlerts ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-19646">
                          {pref.receiveSystemAlerts ? 'Alerts On' : 'Alerts Off'}
                        </span>
                      </div>
                    </div>
                    {pref.mutedEvents.length > 0 && <div className="mt-2" data-id="element-19647">
                        <p className="text-xs text-gray-500" data-id="element-19648">
                          Muted events: {pref.mutedEvents.length}
                        </p>
                      </div>}
                    {pref.updatedAt && <div className="mt-2" data-id="element-19649">
                        <p className="text-xs text-gray-500" data-id="element-19650">
                          Last updated: {formatDateTime(pref.updatedAt, locale)}
                        </p>
                      </div>}
                  </div>}
              </li>)}
        </ul>
      </div>
    </div>;
}